import React, { useMemo, useState } from 'react';

import { PencilIcon, PlusCircleIcon } from 'lucide-react';

import {
  AppThemeEnum,
  Card,
  CardContent,
  CardHeader,
  cn,
  useTheme,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  facebookBaseUrl,
  gitHubBaseUrl,
  httpsScheme,
  linkedInBaseUrl,
  tikTokBaseUrl,
  twitterBaseUrl,
  youTubeBaseUrl,
} from '../../social';
import { SocialLinks } from '@sandtech-hub-monorepo/ehub-client';
import { ReactComponent as FacebookIcon } from '../../assets/Icons/facebook-icon.svg';
import { ReactComponent as XIcon } from '../../assets/Icons/x-icon.svg';
import { ReactComponent as XIconWhite } from '../../assets/Icons/x-icon-white.svg';
import { ReactComponent as TwitterBirdIcon } from '../../assets/Icons/twitter-bird-icon.svg';
import { ReactComponent as InstagramIcon } from '../../assets/Icons/instagram-icon.svg';
import { ReactComponent as TikTokIcon } from '../../assets/Icons/tiktok-icon.svg';
import { ReactComponent as YouTubeIcon } from '../../assets/Icons/youtube-icon.svg';
import { ReactComponent as GithubIcon } from '../../assets/Icons/github-icon.svg';
import { ReactComponent as GithubIconWhite } from '../../assets/Icons/github-icon-white.svg';
import { ReactComponent as LinkedInIcon } from '../../assets/Icons/linkedin-icon.svg';
import { SocialProfilesSectionEditDialog } from '../Edit/SocialProfilesSectionEditDialog';

export const SocialProfileSection = ({
  editable,
  socialLinks,
  defaultOpen = false,
  variant = 'brand',
  instagram,
}: {
  editable?: boolean;
  socialLinks?: SocialLinks;
  defaultOpen?: boolean;
  /**
   * 'monochrome' renders every icon in primary blue and uses the classic
   * Twitter bird — matching the FIT design. Defaults to the multi-colour brand
   * icons so central-hub is unaffected.
   */
  variant?: 'brand' | 'monochrome';
  /**
   * Optional Instagram handle. There is no backend column for it yet; the slot
   * is wired so it renders as soon as one lands.
   */
  instagram?: string;
}) => {
  const { theme } = useTheme();
  const mono = variant === 'monochrome';
  const emptySocials = useMemo(
    () =>
      !socialLinks?.linkedIn &&
      !socialLinks?.facebook &&
      !socialLinks?.twitter &&
      !socialLinks?.youtube &&
      !socialLinks?.tikTok &&
      !socialLinks?.github &&
      !instagram,
    [socialLinks, instagram]
  );
  const [editDialogOpen, setEditDialogOpen] = useState(defaultOpen);

  const monoIcon = 'h-8 w-8 fill-primary';

  return (
    <>
      <Card className="border-border-secondary rounded-sm flex-1">
        <CardHeader className="flex-row justify-between items-center font-semibold text-lg">
          <div>Social Profiles</div>
          {editable && (
            <div
              onClick={() => setEditDialogOpen(true)}
              className="h-10 w-10 p-2 rounded-full hover:bg-muted cursor-pointer text-muted-foreground"
            >
              {emptySocials ? <PlusCircleIcon /> : <PencilIcon />}
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            {socialLinks?.linkedIn && (
              <a
                href={httpsScheme + linkedInBaseUrl + socialLinks.linkedIn}
                target="_blank"
                rel="noreferrer"
              >
                <LinkedInIcon
                  className={cn(mono ? monoIcon : 'fill-[#1877F2]')}
                />
              </a>
            )}

            {socialLinks?.facebook && (
              <a
                href={httpsScheme + facebookBaseUrl + socialLinks.facebook}
                target="_blank"
                rel="noreferrer"
              >
                <FacebookIcon
                  className={cn(mono ? monoIcon : 'fill-[#1877F2]')}
                />
              </a>
            )}

            {instagram && (
              <a
                href={`${httpsScheme}www.instagram.com/${instagram}`}
                target="_blank"
                rel="noreferrer"
              >
                <InstagramIcon className={mono ? 'h-8 w-8' : 'h-6 w-6'} />
              </a>
            )}

            {socialLinks?.twitter && (
              <a
                href={httpsScheme + twitterBaseUrl + socialLinks.twitter}
                target="_blank"
                rel="noreferrer"
              >
                {mono ? (
                  <TwitterBirdIcon className="h-8 w-8" />
                ) : theme === AppThemeEnum.dark ? (
                  <XIconWhite className="w-6 h-6" />
                ) : (
                  <XIcon />
                )}
              </a>
            )}

            {socialLinks?.youtube && (
              <a
                href={httpsScheme + youTubeBaseUrl + socialLinks.youtube}
                target="_blank"
                rel="noreferrer"
              >
                <YouTubeIcon className={mono ? monoIcon : ''} />
              </a>
            )}

            {socialLinks?.tikTok && (
              <a
                href={httpsScheme + tikTokBaseUrl + socialLinks.tikTok}
                target="_blank"
                rel="noreferrer"
              >
                <TikTokIcon className={mono ? monoIcon : ''} />
              </a>
            )}

            {socialLinks?.github && (
              <a
                href={httpsScheme + gitHubBaseUrl + socialLinks.github}
                target="_blank"
                rel="noreferrer"
              >
                {mono ? (
                  <GithubIcon className={monoIcon} />
                ) : theme === AppThemeEnum.dark ? (
                  <GithubIconWhite className="h-6 w-6" />
                ) : (
                  <GithubIcon className="h-6 w-6" />
                )}
              </a>
            )}
          </div>
        </CardContent>
      </Card>
      {editable && (
        <SocialProfilesSectionEditDialog
          defaultValues={socialLinks}
          open={editDialogOpen}
          handleOpenChange={(open) => setEditDialogOpen(open)}
        />
      )}
    </>
  );
};
